import { type ClipboardEvent, useEffect, useMemo, useRef } from "react";
import type { WordBox } from "../../api/types";
import { joinSelected, layoutOcrWords } from "../../lib/ocr-layout";

let measureContext: CanvasRenderingContext2D | null | undefined;

/** Width of the text at 100px in the layer's font (global.css .ocr-text-layer). */
function textWidth100(text: string): number {
  if (measureContext === undefined)
    measureContext = document.createElement("canvas").getContext("2d");
  if (!measureContext) return 0;
  measureContext.font = "100px sans-serif";
  return measureContext.measureText(text).width;
}

const isFirefox = typeof navigator !== "undefined" && navigator.userAgent.includes("Firefox");

/**
 * Over the gaps between words the pointer hits the layer itself, and Chrome extends the selection
 * to the start or the end of the page. As in pdf.js, a block covering the layer during a selection
 * is moved right after the word being selected, so a gap resolves to that word. Firefox handles
 * gaps by itself.
 */
function useSelectionAnchor(layerRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    // Created here, not rendered: React must not own a node that moves between the words.
    const end = document.createElement("div");
    end.className = "ocr-end";
    layer.append(end);
    const controller = new AbortController();
    const { signal } = controller;
    let pointerDown = false;
    let prevRange: Range | null = null;
    const reset = () => {
      layer.append(end);
      layer.classList.remove("selecting");
      prevRange = null;
    };
    document.addEventListener("pointerdown", () => (pointerDown = true), { signal });
    document.addEventListener(
      "pointerup",
      () => {
        pointerDown = false;
        reset();
      },
      { signal },
    );
    window.addEventListener("blur", () => (pointerDown = false), { signal });
    document.addEventListener(
      "selectionchange",
      () => {
        const selection = document.getSelection();
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        if (!range || !range.intersectsNode(layer)) {
          if (!pointerDown) reset();
          return;
        }
        layer.classList.add("selecting");
        if (!pointerDown || isFirefox) return;
        // The edge that moved since the previous change is the one being dragged.
        const modifyStart =
          prevRange !== null &&
          (range.compareBoundaryPoints(Range.END_TO_END, prevRange) === 0 ||
            range.compareBoundaryPoints(Range.START_TO_END, prevRange) === 0);
        let anchor: Node | null = modifyStart ? range.startContainer : range.endContainer;
        if (anchor.nodeType === Node.TEXT_NODE) anchor = anchor.parentNode;
        if (!modifyStart && range.endOffset === 0 && anchor !== layer)
          anchor = anchor?.previousSibling ?? null;
        if (anchor && anchor !== end && anchor.parentNode === layer)
          layer.insertBefore(end, modifyStart ? anchor : anchor.nextSibling);
        prevRange = range.cloneRange();
      },
      { signal },
    );
    return () => {
      controller.abort();
      end.remove();
    };
  }, [layerRef]);
}

/**
 * Invisible, selectable text over a scanned page, built from the OCR word boxes, so a fragment
 * can be selected on the image and copied (a scan has no usable pdf.js text layer). The copied
 * text is assembled from the words with line breaks, not from the absolutely placed spans.
 */
export function OcrTextLayer({
  words,
  width,
  height,
}: {
  words: readonly WordBox[];
  /** Rendered page size in pixels. */
  width: number;
  height: number;
}) {
  const layerRef = useRef<HTMLDivElement>(null);
  useSelectionAnchor(layerRef);
  const spans = useMemo(
    () => layoutOcrWords(words).map((s) => ({ ...s, width100: textWidth100(s.text) })),
    [words],
  );

  const onCopy = (e: ClipboardEvent<HTMLDivElement>) => {
    const selection = document.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
    const range = selection.getRangeAt(0);
    const layer = e.currentTarget;
    // A selection that runs on outside the page keeps the browser's copy.
    if (!layer.contains(range.startContainer) || !layer.contains(range.endContainer)) return;
    const parts: { text: string; eol: boolean }[] = [];
    for (const el of layer.querySelectorAll<HTMLElement>("span[data-i]")) {
      const span = spans[Number(el.dataset.i)];
      if (!span || !range.intersectsNode(el)) continue;
      const node = el.firstChild;
      const start = range.startContainer === node ? range.startOffset : 0;
      const stop = range.endContainer === node ? range.endOffset : span.text.length;
      const text = span.text.slice(start, stop);
      if (text) parts.push({ text, eol: span.eol });
    }
    if (parts.length === 0) return;
    e.clipboardData.setData("text/plain", joinSelected(parts));
    e.preventDefault();
  };

  return (
    <div ref={layerRef} className="ocr-text-layer" onCopy={onCopy}>
      {spans.map((s, i) => {
        const fontPx = s.height * height;
        const natural = (s.width100 * fontPx) / 100;
        return (
          <span
            key={i}
            data-i={i}
            style={{
              left: `${s.left * 100}%`,
              top: `${s.top * 100}%`,
              fontSize: `${fontPx}px`,
              transform: `scaleX(${natural > 0 ? (s.width * width) / natural : 1})`,
            }}
          >
            {s.text}
          </span>
        );
      })}
    </div>
  );
}
